import { useEffect, useState } from "react";
import axios from "axios";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, UserCircle, Mail, Shield, ShieldCheck, KeyRound, X, AlertTriangle, CheckCircle2, Loader2, Tag as TagIcon, Check } from "lucide-react";

export default function TeamPanel({ API, token, currentUser }) {
  const [members, setMembers] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", password: "" });
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [resetFor, setResetFor] = useState(null);
  const [newPwd, setNewPwd] = useState("");
  const [tagForm, setTagForm] = useState({ name: "", color: "#E50914" });
  const [tagMgrOpen, setTagMgrOpen] = useState(false);
  const [tagPickerFor, setTagPickerFor] = useState(null);

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = async () => {
    setLoading(true);
    try {
      const [m, t] = await Promise.all([
        axios.get(`${API}/admin/team`, auth),
        axios.get(`${API}/admin/role-tags`, auth),
      ]);
      setMembers(m.data);
      setTags(t.data);
    } catch (e) { /* noop */ }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const flash = (msg, good = true) => {
    if (good) { setOk(msg); setErr(""); } else { setErr(msg); setOk(""); }
    setTimeout(() => { setOk(""); setErr(""); }, 3500);
  };

  const addMember = async (e) => {
    e.preventDefault();
    setAdding(true);
    setErr("");
    try {
      await axios.post(`${API}/admin/team`, form, auth);
      setAddOpen(false);
      setForm({ email: "", name: "", password: "" });
      flash("Team member added successfully");
      load();
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Failed to add member");
    }
    setAdding(false);
  };

  const toggleActive = async (member) => {
    try {
      await axios.patch(`${API}/admin/team/${member.id}`, { is_active: !member.is_active }, auth);
      flash(`${member.name} ${!member.is_active ? "enabled" : "disabled"}`);
      load();
    } catch (e) {
      flash(e.response?.data?.detail || "Toggle failed", false);
    }
  };

  const togglePermission = async (member, feature) => {
    const current = member.permissions?.[feature] !== false;
    try {
      await axios.patch(`${API}/admin/team/${member.id}/permissions`, { [feature]: !current }, auth);
      flash(`${member.name} — ${feature.replace("_", " ")} ${!current ? "enabled" : "disabled"}`);
      load();
    } catch (e) {
      flash(e.response?.data?.detail || "Permission toggle failed", false);
    }
  };

  const deleteMember = async (member) => {
    if (!window.confirm(`Delete ${member.name} (${member.email})? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/team/${member.id}`, auth);
      flash(`${member.name} removed from team`);
      load();
    } catch (e) {
      flash(e.response?.data?.detail || "Delete failed", false);
    }
  };

  const resetPassword = async () => {
    if (newPwd.length < 6) { flash("Password must be 6+ chars", false); return; }
    try {
      await axios.patch(`${API}/admin/team/${resetFor.id}`, { password: newPwd }, auth);
      flash(`Password reset for ${resetFor.name}`);
      setResetFor(null);
      setNewPwd("");
    } catch (e) {
      flash(e.response?.data?.detail || "Reset failed", false);
    }
  };

  const createTag = async (e) => {
    e.preventDefault();
    if (!tagForm.name.trim()) return;
    try {
      await axios.post(`${API}/admin/role-tags`, tagForm, auth);
      setTagForm({ name: "", color: "#E50914" });
      flash("Tag created");
      load();
    } catch (e2) {
      flash(e2.response?.data?.detail || "Tag create failed", false);
    }
  };

  const deleteTag = async (tag) => {
    if (!window.confirm(`Delete tag "${tag.name}"? It will be removed from all members.`)) return;
    try {
      await axios.delete(`${API}/admin/role-tags/${tag.id}`, auth);
      flash(`Tag "${tag.name}" deleted`);
      load();
    } catch (e) {
      flash(e.response?.data?.detail || "Delete failed", false);
    }
  };

  const toggleMemberTag = async (member, tagId) => {
    const current = member.tag_ids || [];
    const next = current.includes(tagId) ? current.filter((x) => x !== tagId) : [...current, tagId];
    try {
      await axios.patch(`${API}/admin/team/${member.id}/tags`, { tag_ids: next }, auth);
      load();
    } catch (e) {
      flash(e.response?.data?.detail || "Tag update failed", false);
    }
  };

  const updateMemberPublic = async (member, patch) => {
    try {
      await axios.patch(`${API}/admin/team/${member.id}`, patch, auth);
      flash(`${member.name} public profile updated`);
      load();
    } catch (e) {
      flash(e.response?.data?.detail || "Update failed", false);
    }
  };

  const admins = members.filter((m) => m.role === "admin");
  const regulars = members.filter((m) => m.role !== "admin");

  return (
    <div className="space-y-6" data-testid="team-panel">
      {/* Top bar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-black uppercase tracking-tight text-white">Team</h2>
          <p className="text-xs text-zinc-500 mt-1">Manage who has access to this admin panel</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setTagMgrOpen(!tagMgrOpen)}
            data-testid="toggle-tag-manager"
            className="bg-[#141414] text-white font-bold uppercase tracking-widest py-3 px-4 border-2 border-white/20 hover:border-white text-xs flex items-center gap-2"
          >
            <TagIcon size={13} /> Manage Tags ({tags.length})
          </button>
          <button
            onClick={() => setAddOpen(true)}
            data-testid="add-team-member-btn"
            className="btn-brutal bg-red-600 text-white font-bold uppercase tracking-widest py-3 px-5 border-2 border-white hover:bg-white hover:text-black text-xs flex items-center gap-2"
          >
            <Plus size={14} /> Add Member
          </button>
        </div>
      </div>

      {/* Tag manager collapsible */}
      {tagMgrOpen && (
        <div className="border border-white/10 bg-[#0E0E0E] p-5" data-testid="tag-manager">
          <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-white mb-4 flex items-center gap-2"><TagIcon size={11} /> Role Tags</h3>
          <form onSubmit={createTag} className="flex flex-wrap gap-2 mb-4">
            <Input
              data-testid="tag-name-input"
              value={tagForm.name}
              onChange={(e) => setTagForm({ ...tagForm, name: e.target.value.slice(0, 40) })}
              placeholder="Tag name (e.g. CFO, Intern)"
              className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0 flex-1 min-w-[180px]"
            />
            <input
              type="color"
              value={tagForm.color}
              onChange={(e) => setTagForm({ ...tagForm, color: e.target.value })}
              data-testid="tag-color-input"
              className="w-12 h-10 border border-white/20 bg-transparent cursor-pointer"
            />
            <button type="submit" data-testid="tag-create-btn" className="bg-red-600 text-white font-bold uppercase tracking-widest px-4 py-2 border-2 border-white hover:bg-white hover:text-black text-xs flex items-center gap-2">
              <Plus size={13} /> Add
            </button>
          </form>
          {tags.length === 0 ? (
            <p className="text-zinc-600 text-[11px] uppercase tracking-widest text-center py-4">No tags yet. Create one above.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {tags.map((t) => (
                <span
                  key={t.id}
                  data-testid={`tag-chip-${t.id}`}
                  className="inline-flex items-center gap-2 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-white group"
                  style={{ backgroundColor: t.color + "20", borderLeft: `3px solid ${t.color}` }}
                >
                  <span>{t.name}</span>
                  <button
                    onClick={() => deleteTag(t)}
                    data-testid={`tag-delete-${t.id}`}
                    className="text-white/50 hover:text-red-500 transition-colors"
                    title="Delete tag"
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Flash messages */}
      {(ok || err) && (
        <div data-testid="team-flash" className={`border-2 px-4 py-3 flex items-center gap-3 ${ok ? "border-green-500/50 bg-green-500/10 text-green-400" : "border-red-600/50 bg-red-600/10 text-red-400"}`}>
          {ok ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
          <span className="text-sm">{ok || err}</span>
        </div>
      )}

      {/* Quick stats (hidden during initial load to avoid '0' flash) */}
      {!loading && (
        <div className="grid grid-cols-3 gap-3">
          <div className="border border-white/10 bg-[#141414] p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Total Members</p>
            <p className="text-2xl font-black text-white mt-1">{members.length}</p>
          </div>
          <div className="border border-white/10 bg-[#141414] p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Active</p>
            <p className="text-2xl font-black text-green-400 mt-1">{members.filter((m) => m.is_active).length}</p>
          </div>
          <div className="border border-white/10 bg-[#141414] p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Disabled</p>
            <p className="text-2xl font-black text-red-500 mt-1">{members.filter((m) => !m.is_active).length}</p>
          </div>
        </div>
      )}

      {/* Admins section */}
      {admins.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-red-500">Admins · {admins.length}</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {admins.map((m) => (
              <MemberCard
                key={m.id}
                member={m}
                allTags={tags}
                isSelf={m.id === currentUser?._id || m.email === currentUser?.email}
                isAdmin
                onToggleTag={(tagId) => toggleMemberTag(m, tagId)}
                onUpdatePublic={(patch) => updateMemberPublic(m, patch)}
                tagPickerOpen={tagPickerFor === m.id}
                setTagPickerOpen={(o) => setTagPickerFor(o ? m.id : null)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Members section */}
      <div className="space-y-3">
        <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">Team Members · {regulars.length}</h3>
        {loading ? (
          <div className="text-center py-12 text-zinc-500 text-xs uppercase tracking-widest"><Loader2 size={14} className="inline animate-spin mr-2" /> Loading…</div>
        ) : regulars.length === 0 ? (
          <div className="border border-dashed border-white/10 p-10 text-center">
            <UserCircle size={28} className="text-zinc-700 mx-auto mb-2" />
            <p className="text-sm text-zinc-500">No team members yet.</p>
            <p className="text-[11px] text-zinc-600 mt-1">Click "Add Member" to invite your first teammate.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {regulars.map((m) => (
              <MemberCard
                key={m.id}
                member={m}
                allTags={tags}
                onToggle={() => toggleActive(m)}
                onDelete={() => deleteMember(m)}
                onReset={() => { setResetFor(m); setNewPwd(""); }}
                onTogglePermission={(feature) => togglePermission(m, feature)}
                onToggleTag={(tagId) => toggleMemberTag(m, tagId)}
                onUpdatePublic={(patch) => updateMemberPublic(m, patch)}
                tagPickerOpen={tagPickerFor === m.id}
                setTagPickerOpen={(o) => setTagPickerFor(o ? m.id : null)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add modal */}
      {addOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setAddOpen(false)}>
          <div className="border border-white/10 bg-[#0A0A0A] w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="add-member-modal">
            <div className="flex items-start justify-between mb-5">
              <div>
                <h3 className="text-sm font-black uppercase tracking-[0.18em] text-white">Add Team Member</h3>
                <p className="text-[11px] text-zinc-500 mt-1">They'll sign in at /admin/login</p>
              </div>
              <button onClick={() => setAddOpen(false)} className="text-zinc-500 hover:text-white"><X size={18} /></button>
            </div>
            <form onSubmit={addMember} className="space-y-4">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Name</Label>
                <Input data-testid="add-member-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="e.g. Ritu Sharma" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Email</Label>
                <Input data-testid="add-member-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required type="email" placeholder="teammate@kokosites.in" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0" />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Temporary Password</Label>
                <Input data-testid="add-member-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required type="text" placeholder="At least 6 characters" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0 font-mono" />
                <p className="text-[10px] text-zinc-600 mt-1.5">Share this with them securely. They can change it later.</p>
              </div>
              {err && <div className="bg-red-600/10 border border-red-600/40 px-3 py-2 text-xs text-red-400">{err}</div>}
              <button type="submit" disabled={adding} data-testid="add-member-submit" className="w-full bg-red-600 text-white font-bold uppercase tracking-widest py-3 px-4 border-2 border-white hover:bg-white hover:text-black transition-colors text-xs disabled:opacity-50 flex items-center justify-center gap-2">
                {adding ? <><Loader2 size={14} className="animate-spin" /> Adding…</> : <><Plus size={14} /> Create Member</>}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Reset password modal */}
      {resetFor && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setResetFor(null)}>
          <div className="border border-white/10 bg-[#0A0A0A] w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between mb-5">
              <div>
                <h3 className="text-sm font-black uppercase tracking-[0.18em] text-white">Reset Password</h3>
                <p className="text-[11px] text-zinc-500 mt-1">For {resetFor.email}</p>
              </div>
              <button onClick={() => setResetFor(null)} className="text-zinc-500 hover:text-white"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">New Password</Label>
                <Input data-testid="reset-pwd-input" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} type="text" placeholder="At least 6 characters" className="bg-transparent border-white/20 rounded-none text-white text-sm focus-visible:ring-0 font-mono" />
              </div>
              <button onClick={resetPassword} data-testid="reset-pwd-submit" className="w-full bg-red-600 text-white font-bold uppercase tracking-widest py-3 px-4 border-2 border-white hover:bg-white hover:text-black transition-colors text-xs flex items-center justify-center gap-2">
                <KeyRound size={14} /> Update Password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MemberCard({ member, allTags = [], isSelf = false, isAdmin = false, onToggle, onDelete, onReset, onTogglePermission, onToggleTag, onUpdatePublic, tagPickerOpen, setTagPickerOpen }) {
  const [permsOpen, setPermsOpen] = useState(false);
  const [publicOpen, setPublicOpen] = useState(false);
  const [titleDraft, setTitleDraft] = useState(member.public_title || "");
  const initial = (member.display_name || member.name || member.email || "?").slice(0, 1).toUpperCase();
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const avatarSrc = member.avatar_url ? `${BACKEND}${member.avatar_url}` : null;

  const permissionList = [
    { key: "reviews", label: "Reviews" },
    { key: "contacts", label: "Messages" },
    { key: "payments", label: "Payments" },
    { key: "orders", label: "Orders" },
    { key: "clients", label: "Clients" },
    { key: "tasks", label: "Tasks" },
    { key: "ai_command", label: "AI Command" },
    { key: "ai_email", label: "AI Email" },
    { key: "email_health", label: "Email Health" },
    { key: "analytics_revenue", label: "Revenue Tile" },
  ];
  const enabledCount = permissionList.filter((p) => member.permissions?.[p.key] !== false).length;
  const memberTags = member.tags || [];
  const memberTagIds = member.tag_ids || [];

  return (
    <div
      data-testid={`member-card-${member.id}`}
      className={`relative border bg-gradient-to-b from-[#141414] to-[#0E0E0E] p-5 ${member.is_active ? "border-white/10 hover:border-white/30" : "border-red-900/30 bg-red-900/5 opacity-80"} transition-colors`}
    >
      {isAdmin && (
        <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-[0.18em] text-red-400 bg-red-600/10 border border-red-600/40 px-2 py-0.5">
          <ShieldCheck size={10} /> Admin
        </span>
      )}
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 shrink-0 flex items-center justify-center text-lg font-black overflow-hidden ${isAdmin ? "bg-red-600 text-white" : "bg-white/5 text-white border border-white/10"}`}>
          {avatarSrc ? <img src={avatarSrc} alt={member.name} className="w-full h-full object-cover" /> : initial}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-white truncate">{member.display_name || member.name}</p>
          <p className="text-xs text-zinc-500 truncate flex items-center gap-1 mt-0.5"><Mail size={11} /> {member.email}</p>
          <p className="text-[10px] text-zinc-600 mt-2">
            {member.last_login_at ? `Last login: ${new Date(member.last_login_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}` : "Never signed in"}
          </p>
          {/* Tag chips */}
          {(memberTags.length > 0 || allTags.length > 0) && (
            <div className="flex flex-wrap items-center gap-1.5 mt-3">
              {memberTags.map((t) => (
                <span
                  key={t.id}
                  data-testid={`member-tag-${t.id}-${member.id}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white"
                  style={{ backgroundColor: t.color + "25", borderLeft: `2px solid ${t.color}` }}
                >
                  {t.name}
                </span>
              ))}
              {allTags.length > 0 && onToggleTag && (
                <button
                  onClick={() => setTagPickerOpen(!tagPickerOpen)}
                  data-testid={`tag-picker-toggle-${member.id}`}
                  className="inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-zinc-400 border border-dashed border-white/20 hover:text-white hover:border-white/40 transition-colors"
                >
                  <TagIcon size={9} /> {memberTags.length === 0 ? "Assign tag" : "Edit"}
                </button>
              )}
            </div>
          )}
          {/* Tag picker popover */}
          {tagPickerOpen && allTags.length > 0 && (
            <div
              data-testid={`tag-picker-${member.id}`}
              className="mt-2 border border-white/10 bg-black/80 p-2 space-y-1 max-h-44 overflow-y-auto"
            >
              {allTags.map((t) => {
                const on = memberTagIds.includes(t.id);
                return (
                  <button
                    key={t.id}
                    onClick={() => onToggleTag(t.id)}
                    data-testid={`tag-option-${t.id}-${member.id}`}
                    className={`w-full flex items-center justify-between px-2 py-1.5 text-[11px] uppercase tracking-widest transition-colors ${on ? "bg-white/10 text-white" : "text-zinc-400 hover:bg-white/5 hover:text-white"}`}
                  >
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2" style={{ backgroundColor: t.color }} />
                      {t.name}
                    </span>
                    {on && <Check size={12} className="text-green-400" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {!isAdmin && (
        <>
          <div className="mt-4 pt-4 border-t border-white/5 flex items-center justify-between gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <Switch
                checked={member.is_active}
                onCheckedChange={onToggle}
                data-testid={`toggle-${member.id}`}
                className="data-[state=checked]:bg-green-500 data-[state=unchecked]:bg-zinc-700"
              />
              <span className={`text-[10px] font-black uppercase tracking-widest ${member.is_active ? "text-green-400" : "text-zinc-500"}`}>
                {member.is_active ? "Active" : "Disabled"}
              </span>
            </label>
            <div className="flex items-center gap-1">
              <button
                onClick={onReset}
                data-testid={`reset-${member.id}`}
                className="p-2 text-zinc-500 hover:text-white hover:bg-white/5 transition-colors"
                title="Reset password"
              >
                <KeyRound size={14} />
              </button>
              <button
                onClick={onDelete}
                data-testid={`delete-${member.id}`}
                className="p-2 text-zinc-500 hover:text-red-500 hover:bg-red-600/10 transition-colors"
                title="Remove from team"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>

          {/* Permissions expander */}
          <button
            onClick={() => setPermsOpen(!permsOpen)}
            data-testid={`perms-toggle-${member.id}`}
            className="mt-3 w-full flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-zinc-400 hover:text-white border border-white/10 hover:border-white/30 px-3 py-2 transition-colors"
          >
            <span className="flex items-center gap-2"><Shield size={11} /> Feature Access · {enabledCount}/{permissionList.length}</span>
            <span className="text-zinc-500">{permsOpen ? "−" : "+"}</span>
          </button>
          {permsOpen && (
            <div className="mt-3 space-y-2" data-testid={`perms-panel-${member.id}`}>
              {permissionList.map((p) => {
                const enabled = member.permissions?.[p.key] !== false;
                return (
                  <div key={p.key} className="flex items-center justify-between px-3 py-2 border border-white/5 bg-black/20">
                    <span className="text-[11px] uppercase tracking-wider text-zinc-300">{p.label}</span>
                    <Switch
                      checked={enabled}
                      onCheckedChange={() => onTogglePermission(p.key)}
                      data-testid={`perm-${p.key}-${member.id}`}
                      className="data-[state=checked]:bg-green-500 data-[state=unchecked]:bg-zinc-700 scale-75"
                    />
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {isAdmin && isSelf && (
        <p className="mt-4 pt-4 border-t border-white/5 text-[10px] text-zinc-600 uppercase tracking-widest flex items-center gap-2">
          <Shield size={11} /> That's you — full access
        </p>
      )}

      {onUpdatePublic && (
        <>
          <button
            onClick={() => setPublicOpen(!publicOpen)}
            data-testid={`public-toggle-${member.id}`}
            className="mt-3 w-full flex items-center justify-between text-[10px] font-bold uppercase tracking-widest text-zinc-400 hover:text-white border border-white/10 hover:border-white/30 px-3 py-2 transition-colors"
          >
            <span className="flex items-center gap-2"><UserCircle size={11} /> Public Profile · {member.public_profile ? "Visible on site" : "Hidden"}</span>
            <span className="text-zinc-500">{publicOpen ? "−" : "+"}</span>
          </button>
          {publicOpen && (
            <div className="mt-3 p-3 border border-white/10 bg-black/20 space-y-3" data-testid={`public-panel-${member.id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] font-bold text-white">Show on "Meet the Team"</p>
                  <p className="text-[10px] text-zinc-500 mt-0.5 leading-relaxed">Appears in the public team section on the landing page.</p>
                </div>
                <Switch
                  checked={!!member.public_profile}
                  onCheckedChange={(v) => onUpdatePublic({ public_profile: v })}
                  data-testid={`public-profile-switch-${member.id}`}
                  className="data-[state=checked]:bg-red-600 data-[state=unchecked]:bg-zinc-700"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Public Title</Label>
                <div className="flex gap-2">
                  <Input
                    value={titleDraft}
                    onChange={(e) => setTitleDraft(e.target.value)}
                    placeholder="Founder & Lead Designer"
                    maxLength={60}
                    data-testid={`public-title-input-${member.id}`}
                    className="bg-transparent border-white/20 rounded-none text-white text-xs focus-visible:ring-0 flex-1"
                  />
                  <button
                    type="button"
                    onClick={() => onUpdatePublic({ public_title: titleDraft })}
                    data-testid={`public-title-save-${member.id}`}
                    className="tap text-[10px] font-bold uppercase tracking-widest border border-white/20 hover:border-white/40 text-zinc-300 hover:text-white px-3"
                  >
                    Save
                  </button>
                </div>
                <p className="text-[10px] text-zinc-600 mt-1">Shown under your name on the site. Leave blank to fall back to your primary tag.</p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
